import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Routes, Route } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, MessageSquare, GitCommit } from "lucide-react";
import BackButton from "@/components/business/BackButton";
import LoadingState from "@/components/business/LoadingState";
import PageHeader from "@/components/business/PageHeader";
import EmptyState from "@/components/business/EmptyState";
import ReviewActionBar from "@/components/business/ReviewActionBar";

const STATE_LABEL = {
  ai_generated: "AI generated · awaiting review",
  ca_modified: "CA modified · awaiting review",
  ca_approved: "CA approved",
  client_released: "Released",
  filed_completed: "Filed",
  rejected: "Rejected",
};
const STATE_COLOR = {
  ai_generated: "bg-blue-100 text-blue-800 border-blue-300",
  ca_modified: "bg-purple-100 text-purple-800 border-purple-300",
  ca_approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  client_released: "bg-emerald-500 text-white border-emerald-500",
  filed_completed: "bg-slate-800 text-white border-slate-800",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
};

export default function Approvals() {
  return (
    <Routes>
      <Route index element={<Inbox />} />
      <Route path=":approvalId" element={<Detail />} />
    </Routes>
  );
}

function Inbox() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/approvals").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const pending = rows.filter(r => ["ai_generated", "ca_modified"].includes(r.state));

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <PageHeader>
          <PageHeader.Title>Review Center</PageHeader.Title>
          <PageHeader.Actions>
            <Badge variant="outline"><ShieldCheck className="h-3 w-3 mr-1" />CA-required</Badge>
          </PageHeader.Actions>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
          Every filing, notice response, tax planning report, and client-facing document
          requires <span className="font-semibold">explicit CA approval</span> before release.
          Internal work runs freely — only artifacts leaving the platform pass through this queue.
        </p>

        <Card className="mt-6 border-border overflow-hidden">
          <div className="bg-muted/40 px-4 py-2 text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">
            Pending ({pending.length})
          </div>
          <table className="w-full text-sm">
            <thead className="bg-background text-xs uppercase text-muted-foreground">
              <tr><th className="text-left p-3">Kind</th><th className="text-left p-3">Title</th><th className="text-left p-3">Risk</th><th className="text-left p-3">State</th><th></th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5}>
                  <EmptyState icon={ShieldCheck} title="Nothing awaiting approval." description="All clear — no documents are pending CA review." className="py-8" />
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border" data-testid={`approval-row-${r.id.slice(0, 6)}`}>
                  <td className="p-3 font-semibold uppercase text-xs">{r.kind.replace(/_/g, " ")}</td>
                  <td className="p-3">{r.title}</td>
                  <td className="p-3">
                    <Badge variant="outline" className={r.risk_tier === "client_release" ? "border-rose-300 text-rose-700" : ""}>
                      {r.risk_tier.replace(/_/g, " ")}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] uppercase tracking-wide ${STATE_COLOR[r.state]}`}>
                      {STATE_LABEL[r.state] || r.state}
                    </span>
                  </td>
                  <td className="p-3 text-right"><Button asChild variant="outline" size="sm"><Link to={`/approvals/${r.id}`}>Review</Link></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

function Detail() {
  const { approvalId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");

  const load = () => api.get(`/approvals/${approvalId}`).then(({ data }) => setD(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [approvalId]);
  if (!d) return <AppShell><LoadingState /></AppShell>;

  const approve = async () => {
    setBusy(true);
    try { await api.post(`/approvals/${approvalId}/approve`, { comment: comment || undefined }); toast.success("Approved — released"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    if (!reason.trim()) return toast.error("Reason required to reject");
    setBusy(true);
    try { await api.post(`/approvals/${approvalId}/reject`, { reason }); toast("Rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const canAct = ["ai_generated", "ca_modified"].includes(d.state);

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
        <BackButton to="/approvals" label="Review Center" />
        <PageHeader>
          <PageHeader.Title eyebrow={d.kind.replace(/_/g, " ")}>{d.title}</PageHeader.Title>
        </PageHeader>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className={`inline-block px-2 py-0.5 rounded border text-[10px] uppercase tracking-wide ${STATE_COLOR[d.state]}`}>
            {STATE_LABEL[d.state]}
          </span>
          <Badge variant="outline">{d.risk_tier.replace(/_/g, " ")}</Badge>
          {d.ai_meta && <Badge variant="outline">AI · {d.ai_meta.provider}/{d.ai_meta.model}</Badge>}
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
          <div className="space-y-4">
            <Card className="p-6 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Snapshot to be released</div>
              <pre className="mt-3 text-xs bg-muted/40 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-96">{JSON.stringify(d.snapshot, null, 2)}</pre>
              <div className="mt-3 text-[10px] text-muted-foreground">SHA-256: <span className="font-mono">{d.fingerprint?.slice(0, 32)}…</span></div>
            </Card>

            {canAct && (
              <ReviewActionBar
                onApprove={approve}
                onReject={reject}
                approveComment={comment}
                onApproveCommentChange={(e) => setComment(e.target.value)}
                rejectReason={reason}
                onRejectReasonChange={(e) => setReason(e.target.value)}
                busy={busy}
              />
            )}
          </div>

          <div className="space-y-4">
            <Card className="p-5 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1.5"><GitCommit className="h-3.5 w-3.5" />History</div>
              <ul className="mt-2 space-y-1.5">
                {(d.history || []).map(h => (
                  <li key={h.id} className="text-xs">
                    <span className="font-mono">{new Date(h.at).toLocaleString()}</span>
                    <span className="ml-2 text-muted-foreground">{h.event} → {h.state}</span>
                    {h.comment && <div className="ml-4 italic">"{h.comment}"</div>}
                  </li>
                ))}
              </ul>
            </Card>
            {(d.approvals || []).length > 0 && (
              <Card className="p-5 border-border">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Approval signatures</div>
                <ul className="mt-2 space-y-2">
                  {d.approvals.map((s, i) => (
                    <li key={i} className="text-xs rounded border border-emerald-300 bg-emerald-50 p-2">
                      <div className="font-semibold">{s.approver_email}</div>
                      <div className="text-muted-foreground">{new Date(s.at).toLocaleString()}</div>
                      <div className="font-mono text-[10px] text-muted-foreground truncate">sig {s.payload_hash?.slice(0, 20)}…</div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
